import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { useScrollReveal, useCountUp } from '../hooks/useScrollReveal';
import heroMeeting from '../assets/hero-meeting.jpg';
import heroSigning from '../assets/hero-signing.jpg';
import caseStudy from '../assets/case-study.jpg';
import testimonialAvatar from '../assets/testimonial-avatar.jpg';

const Home = () => {
  useScrollReveal();
  useCountUp();

  useEffect(() => {
    // Feature showcase glyph switching
    const glyphs = {
      shield: '<path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/>',
      scale: '<path d="M12 3v18M5 8l-3 5a4 4 0 0 0 8 0l-3-5Z"/><path d="M19 8l-3 5a4 4 0 0 0 8 0l-3-5Z"/><path d="M5 8h14"/>',
      bell: '<path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z"/><path d="M10 21a2 2 0 0 0 4 0"/>',
      layers: '<path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>'
    };
    
    const glyphEl = document.getElementById('featureGlyph');
    const items = document.querySelectorAll('.feature-item');
    
    const fio = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          items.forEach(i => i.classList.remove('active'));
          entry.target.classList.add('active');
          const icon = entry.target.getAttribute('data-icon');
          if (glyphEl && glyphs[icon]) {
            glyphEl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' + glyphs[icon] + '</svg>';
          }
        }
      });
    }, { threshold: 0.5, rootMargin: '-40% 0px -40% 0px' });
    
    items.forEach(i => fio.observe(i));
    if (items[0]) items[0].classList.add('active');

    return () => fio.disconnect();
  }, []);

  return (
    <>
      <Nav />
      
      <section className="hero">
        <div className="grid-bg"></div>
        <div className="wrap hero-inner">
          <div className="pill reveal in">
            <span className="dot">◆</span> [2026 Outlook] Modernizing legacy federal systems <span className="arrow">→</span>
          </div>
          <h1 className="reveal in">Build mission-ready <span className="accent-word">federal</span> projects.</h1>
          <p className="sub reveal in">The delivery platform built for public-sector teams. Ship secure, compliant, audit-ready projects for federal, state, and local agencies &mdash; without the legacy overhead.</p>
          <div className="hero-ctas reveal in">
            <a href="#contact" className="btn btn-dark">Get Started <span className="arrow">→</span></a>
            <Link to="/valor-os" className="btn btn-outline">Explore Valor OS <span className="arrow">→</span></Link>
          </div>
        </div>

        <div className="wrap showcase reveal">
          {/* Left: photo + live activity chip */}
          <div className="sc-col sc-left">
            <figure className="sc-photo sc-photo-tall">
              <img src={heroMeeting} alt="Agency team reviewing a case file together" />
              <figcaption className="sc-chip sc-chip-bottom">
                <span className="sc-avatar">MR</span>
                <span className="sc-chip-text"><strong>Case #4821 approved</strong><em>Reviewed by M. Reyes &middot; just now</em></span>
              </figcaption>
            </figure>
            <div className="sc-float sc-notify" aria-hidden="true">
              <span className="sc-notify-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z"/><path d="M10 21a2 2 0 0 0 4 0"/></svg>
              </span>
              <span className="sc-chip-text"><strong>3 reviews due today</strong><em>Auto-routed to your queue</em></span>
            </div>
          </div>

          {/* Centre: product dashboard */}
          <div className="sc-col sc-center">
            <div className="sc-dash" role="img" aria-label="Valor OS dashboard preview showing open cases, average resolution time, SLA compliance and a recent case table">
              <div className="sc-dash-bar">
                <span className="sc-dots"><i></i><i></i><i></i></span>
                <span className="sc-dash-title">Valor OS <b>/</b> Programs</span>
                <span className="sc-search">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
                  Search cases
                </span>
              </div>

              <div className="sc-kpis">
                <div className="sc-kpi">
                  <span className="sc-kpi-label">Open cases</span>
                  <span className="sc-kpi-value">1,284</span>
                  <span className="sc-delta up">+12%</span>
                  <svg className="sc-spark" viewBox="0 0 60 20" preserveAspectRatio="none"><polyline points="0,15 8,13 16,14 24,9 32,11 40,6 50,7 60,2" /></svg>
                </div>
                <div className="sc-kpi">
                  <span className="sc-kpi-label">Avg. resolution</span>
                  <span className="sc-kpi-value">2.4<small>d</small></span>
                  <span className="sc-delta good">&minus;38%</span>
                  <svg className="sc-spark" viewBox="0 0 60 20" preserveAspectRatio="none"><polyline points="0,3 9,5 18,4 27,9 36,8 45,13 54,14 60,17" /></svg>
                </div>
                <div className="sc-kpi">
                  <span className="sc-kpi-label">SLA met</span>
                  <span className="sc-kpi-value">99.2<small>%</small></span>
                  <span className="sc-delta good">+0.4</span>
                  <svg className="sc-spark" viewBox="0 0 60 20" preserveAspectRatio="none"><polyline points="0,12 10,11 20,12 30,8 40,9 50,5 60,4" /></svg>
                </div>
              </div>

              <div className="sc-chart">
                <div className="sc-chart-head"><span>Cases closed</span><span className="sc-range">Last 30 days</span></div>
                <svg viewBox="0 0 320 90" preserveAspectRatio="none" className="sc-area">
                  <defs>
                    <linearGradient id="scFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#e8720e" stopOpacity=".28" />
                      <stop offset="100%" stopColor="#e8720e" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <g className="sc-grid-lines"><line x1="0" x2="320" y1="22" y2="22"/><line x1="0" x2="320" y1="45" y2="45"/><line x1="0" x2="320" y1="68" y2="68"/></g>
                  <path d="M0 70 C30 62 50 66 80 50 S130 40 160 44 S220 20 250 26 S300 10 320 6 L320 90 L0 90 Z" fill="url(#scFill)" />
                  <path d="M0 70 C30 62 50 66 80 50 S130 40 160 44 S220 20 250 26 S300 10 320 6" className="sc-line" />
                  <circle cx="320" cy="6" r="4" className="sc-end" />
                </svg>
              </div>

              <table className="sc-table">
                <tbody>
                  <tr><td className="mono">#4821</td><td>Benefits renewal</td><td><span className="sc-status ok">Approved</span></td></tr>
                  <tr><td className="mono">#4820</td><td>Permit review</td><td><span className="sc-status review">In review</span></td></tr>
                  <tr><td className="mono">#4819</td><td>Records request</td><td><span className="sc-status pending">Pending</span></td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: stat + photo + live chip */}
          <div className="sc-col sc-right">
            <div className="sc-stat">
              <span className="sc-stat-value">40%</span>
              <span className="sc-stat-label">faster case turnaround</span>
              <span className="sc-stat-tag"><i></i> FedRAMP-aligned</span>
            </div>
            <figure className="sc-photo sc-photo-wide">
              <img src={heroSigning} alt="Caseworker signing an approval document" />
              <figcaption className="sc-chip sc-chip-top">
                <span className="sc-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10"/></svg>
                </span>
                <span className="sc-chip-text"><strong>Signature captured</strong><em>Audit trail logged</em></span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="trust">
        <div className="wrap trust-inner" style={{flexDirection:'column'}}>
          <p>Trusted by modernization teams across federal, state, and municipal government</p>
          <div className="trust-logos">
            <span>DEPT. OF DIGITAL SVC</span>
            <span>STATE HEALTH AGENCY</span>
            <span>CIVIC INFRA BOARD</span>
            <span>PUBLIC WORKS AUTH.</span>
            <span>REGIONAL TRANSIT COMM.</span>
          </div>
        </div>
      </section>

      <section id="platform">
        <div className="wrap section-head reveal">
          <span className="eyebrow">Valor Core</span>
          <h2>A delivery platform. And so much more.</h2>
        </div>

        <div className="feature-stack">
          <div className="feature-visual">
            <div className="glyph" id="featureGlyph">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/>
              </svg>
            </div>
          </div>
          <div className="feature-items">
            <div className="feature-item" data-icon="shield">
              <span className="num">01</span>
              <h3>Built for compliance, by default.</h3>
              <p>Align to FedRAMP, NIST 800-53, and state-level security frameworks out of the box. Every workflow ships with an audit trail attached.</p>
            </div>
            <div className="feature-item" data-icon="scale">
              <span className="num">02</span>
              <h3>Scale with your mandate.</h3>
              <p>Agencies grow, budgets shift, and requirements change mid-cycle. Valor Core is built to adapt without a re-platform.</p>
            </div>
            <div className="feature-item" data-icon="bell">
              <span className="num">03</span>
              <h3>Keep every stakeholder in the loop.</h3>
              <p>Automated status updates for caseworkers, constituents, and oversight bodies &mdash; tailored to who needs to know what, and when.</p>
            </div>
            <div className="feature-item" data-icon="layers">
              <span className="num">04</span>
              <h3>One system of record.</h3>
              <p>Every change is versioned and traceable. Reconstruct any record's full history for audits, FOIA requests, or internal review.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="fog-section" id="solutions">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Valor OS</span>
            <h2>One home for operating and delivering programs. However complex.</h2>
          </div>
          <div className="grid-6 reveal-stagger">
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6h16M4 12h16M4 18h10"/>
                </svg>
              </div>
              <h3>Case Management</h3>
              <p>Unify intake, review, and disposition into a single case record &mdash; no more swivel-chairing between systems.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/>
                </svg>
              </div>
              <h3>Secure Data Exchange</h3>
              <p>Move sensitive constituent data between agencies and partners with end-to-end encryption and full chain-of-custody logging.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8"/>
                </svg>
              </div>
              <h3>Workflow Automation</h3>
              <p>Design no-code approval chains and task routing so staff spend less time on process, more on constituents.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19V5M4 19h16M8 15v-4M13 15V7M18 15v-8"/>
                </svg>
              </div>
              <h3>Insights &amp; Reporting</h3>
              <p>Generate the reports your oversight board actually asks for &mdash; program KPIs, budget burn, and SLA compliance.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-12M6 9l-3 3 3 3M18 9l3 3-3 3"/>
                </svg>
              </div>
              <h3>Sandbox &amp; Preview</h3>
              <p>Trial new policy logic against historical data before it ever touches a live constituent record.</p>
            </div>
            <div className="grid-card">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z"/>
                </svg>
              </div>
              <h3>Compliance Guard</h3>
              <p>Continuous, always-on checks that flag drift from statutory rules before it becomes a finding.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="customers">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Customer Story</span>
            <h2>Purpose-built for teams modernizing public services.</h2>
          </div>
          <a href="#" className="spotlight reveal">
            <img src={caseStudy} alt="State health agency case study" />
            <div className="spotlight-body">
              <div className="spotlight-tags">
                <span>Case Management</span>
                <span>State &amp; Local</span>
              </div>
              <h3>How a state health agency cut case turnaround 6x with Valor</h3>
              <p>Illustrative customer story &middot; Read more &rarr;</p>
            </div>
          </a>
          <div className="quote-card reveal">
            <p className="q">"We replaced four disconnected legacy tools with one Valor deployment. Casework that took three days now closes same-day &mdash; and every step is auditable."</p>
            <p className="who">
              <img src={testimonialAvatar} alt="Director" />
              Director of Operations &middot; State Health Agency (illustrative)
            </p>
          </div>
        </div>
      </section>

      <section className="fog-section" id="developers">
        <div className="wrap dev-section">
          <div className="reveal">
            <span className="eyebrow">For Developers</span>
            <h2 style={{marginTop:'14px', marginBottom:'20px', fontSize:'clamp(26px,3vw,38px)'}}>Deliver programs faster. With less rework.</h2>
            <p style={{color:'var(--muted)', fontSize:'16px', lineHeight:'1.6', marginBottom:'28px', maxWidth:'460px'}}>A modern, low-code API and component library purpose-built for public-sector procurement and security review &mdash; so your team ships instead of re-justifying architecture.</p>
            <a href="#contact" className="btn btn-accent">View API Docs <span className="arrow">→</span></a>
          </div>
          <div className="code-panel reveal">
            <span className="com">// create a new case record</span><br/>
            <span className="kw">const</span> record = <span className="kw">await</span> valor.cases.<span className="kw">create</span>({'{'}<br/>
            &nbsp;&nbsp;program: <span className="str">"snap_renewal"</span>,<br/>
            &nbsp;&nbsp;jurisdiction: <span className="str">"state:CA"</span>,<br/>
            &nbsp;&nbsp;applicant: applicant.id,<br/>
            &nbsp;&nbsp;priority: <span className="str">"standard"</span><br/>
            {'}'});<br/><br/>
            <span className="com">// automatically compliant &amp; logged</span><br/>
            <span className="kw">await</span> record.<span className="kw">submit</span>();
          </div>
        </div>
      </section>

      <section style={{paddingBottom:0}}>
        <div className="wrap section-head reveal">
          <p style={{maxWidth:'520px',margin:'0 auto 8px',color:'var(--muted)',fontSize:'15px'}}>Built to plug into the infrastructure your agency already runs.</p>
        </div>
      </section>
      
      <div className="marquee-wrap reveal in">
        <div className="marquee" id="marquee">
          <span>CLOUD HOSTING</span><span>IDENTITY &amp; SSO</span><span>PAYMENT RAILS</span><span>DATA WAREHOUSING</span><span>DOCUMENT SIGNING</span><span>CASE ANALYTICS</span><span>NOTIFICATIONS</span>
          <span>CLOUD HOSTING</span><span>IDENTITY &amp; SSO</span><span>PAYMENT RAILS</span><span>DATA WAREHOUSING</span><span>DOCUMENT SIGNING</span><span>CASE ANALYTICS</span><span>NOTIFICATIONS</span>
        </div>
      </div>

      <section>
        <div className="wrap stats reveal-stagger">
          <div className="stat"><h3 data-count="42">0</h3><p>Agencies modernized</p></div>
          <div className="stat"><h3 data-count="99" data-suffix=".9%">0</h3><p>Platform uptime</p></div>
          <div className="stat"><h3 data-count="6" data-suffix="x">0</h3><p>Faster case turnaround</p></div>
          <div className="stat"><h3 data-count="100" data-suffix="%">0</h3><p>Audit-ready by default</p></div>
        </div>
      </section>

      <section className="fog-section" id="resources">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Insights</span>
            <h2>Learn more about next-gen government software.</h2>
          </div>
          <div className="res-grid reveal-stagger">
            <a href="#" className="res-card">
              <div className="res-thumb"></div>
              <div className="res-body">
                <span className="eyebrow">Modernization</span>
                <h4>Why legacy case-management systems are a compliance risk</h4>
              </div>
            </a>
            <a href="#" className="res-card">
              <div className="res-thumb"></div>
              <div className="res-body">
                <span className="eyebrow">Procurement</span>
                <h4>A field guide to public-sector software procurement cycles</h4>
              </div>
            </a>
            <a href="#" className="res-card">
              <div className="res-thumb"></div>
              <div className="res-body">
                <span className="eyebrow">Security</span>
                <h4>Getting to FedRAMP-ready without slowing delivery</h4>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Home;
